import type { SpinCoordinator } from "../SpinCoordinator";
import type { SpinPhase } from "../SpinCoordinatorTypes";
import type { GridLoadingOverlayView } from "./GridLoadingOverlayView";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * Default delay before the loading overlay is shown after entering a
 * network-wait phase. Short enough that genuinely slow responses feel
 * acknowledged, long enough that fast responses don't flash the overlay.
 */
const DEFAULT_SHOW_DELAY_MS = 1500;

/**
 * Owns the lifecycle of `GridLoadingOverlayView`. Subscribes to
 * `SpinCoordinator` and watches the `phase` field; when it enters a
 * network-wait phase (`requesting` or `settling`), schedules a delayed
 * show, and cancels/hides on transition out. Both phases map directly
 * to a network roundtrip:
 * - `requesting` — the spin response fetch (`requestRoundSnapshot`).
 * - `settling`   — the presentation-complete acknowledgement
 *   (`acknowledgePresentationComplete`).
 *
 * The delayed-show timer means the overlay never flashes on
 * fast-responding networks; it only appears once a response has actually
 * exceeded the threshold. The delay rides the chrome runner (pauses with
 * the ticker, never speed-scales).
 */
export class GridLoadingOverlayPresenter {
  private unsubscribe?: () => void;
  private pendingShowGeneration = 0;
  private currentPhase: SpinPhase = "idle";

  public constructor(
    private readonly panel: GridLoadingOverlayView,
    private readonly spinCoordinator: SpinCoordinator,
    private readonly runner: PresentationTaskRunner,
    private readonly showDelayMs: number = DEFAULT_SHOW_DELAY_MS,
  ) {}

  public start(): void {
    // `subscribe` hands over the current snapshot at once: a request already in flight schedules its show here.
    this.unsubscribe = this.spinCoordinator.subscribe((snapshot) => {
      this.handlePhaseChange(snapshot.phase);
    });
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.cancelPendingShow();
    this.panel.hide();
  }

  private handlePhaseChange(phase: SpinPhase): void {
    if (phase === this.currentPhase) return;
    this.currentPhase = phase;

    const isWaitingOnNetwork = phase === "requesting" || phase === "settling";

    if (isWaitingOnNetwork) {
      this.scheduleShow();
    } else {
      this.cancelPendingShow();
      this.panel.hide();
    }
  }

  private scheduleShow(): void {
    const generation = ++this.pendingShowGeneration;
    void this.runner.sleep(this.showDelayMs / 1000).then(() => {
      if (generation !== this.pendingShowGeneration) return;
      this.panel.show();
    });
  }

  private cancelPendingShow(): void {
    this.pendingShowGeneration++;
  }
}
